import { getAccessToken, isAuthenticated } from '../auth/authProvider';
import { getSessionId, getCorrelationId } from '../helpers/helpers';
import config from '../config';

export enum BodyType {
  json = 'json',
  formUrlEncoded = 'formUrlEncoded',
  none = 'none',
}

interface DataObject {
  [key: string]: any;
}

interface RequestOptions {
  headers?: HeadersInit;
  returnStatusCode?: boolean;
  bodyType?: BodyType;
  skipAuth?: boolean; // Optional flag for rare public endpoints
}

class HttpError extends Error {
  constructor(
    public status: number,
    public responseText: string,
    message: string
  ) {
    super(message);
    this.name = 'HttpError';
  }
}

const API_CONFIG = {
  baseUrl: config.API_URL,
  timeout: 30000,
};

export async function buildHeaders(customHeaders?: HeadersInit, skipAuth: boolean = false): Promise<Headers> {
  const headers = new Headers(customHeaders);

  headers.set('Accept', 'application/json');
  headers.set('Content-Type', 'application/json');
  headers.set('bsw-CorrelationId', getCorrelationId() || '');
  headers.set('bsw-SessionId', getSessionId() || '');

  // Add authentication token by default
  if (!skipAuth) {
    const token = await getAccessToken();

    if (!token && isAuthenticated()) {
      throw new Error('No authentication token available');
    }

    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
  }

  return headers;
}

async function handleSuccess(response: Response): Promise<any> {
  const contentType = response.headers.get('content-type');

  if (contentType?.includes('application/json')) {
    const text = await response.text();
    return text ? JSON.parse(text) : {};
  }

  // For non-JSON responses, return the raw text
  return response.text();
}

async function handleError(url: string, response: Response): Promise<never> {
  const contentType = response.headers.get('content-type');
  let errorMessage = `Request to ${url} failed with status ${response.status}`;
  let responseText = '';

  try {
    responseText = await response.text();

    if (contentType?.includes('application/json') && responseText) {
      const errorData = JSON.parse(responseText);
      errorMessage = errorData.message || errorData.Message || errorMessage;
    } else if (responseText) {
      errorMessage = responseText;
    }
  } catch (error) {
    console.error('Parsing failed:', {
      url,
      error,
    });
    throw error;
  }

  throw new HttpError(response.status, responseText, errorMessage);
}

function maskSensitiveData(data: any): any {
  if (!data) return data;

  const sensitiveFields = ['password', 'token', 'apiKey', 'secret'];
  const masked = { ...data };

  for (const field of sensitiveFields) {
    if (field in masked) {
      masked[field] = '***REDACTED***';
    }
  }

  return masked;
}

/**
 * Generic GET request
 */
export async function getJSON<T = any>(url: string, options: RequestOptions = {}): Promise<T> {
  const { headers: customHeaders, returnStatusCode = false, skipAuth = false } = options;

  const fullUrl = url.startsWith('http') ? url : `${API_CONFIG.baseUrl}${url}`;
  const headers = await buildHeaders(customHeaders, skipAuth);

  try {
    const response = await fetch(fullUrl, {
      method: 'GET',
      headers,
      signal: AbortSignal.timeout(API_CONFIG.timeout),
    });

    if (!response.ok) {
      await handleError(fullUrl, response);
    }

    const data = await handleSuccess(response);

    if (returnStatusCode) {
      return { ...data, statusCode: response.status } as T;
    }

    return data as T;
  } catch (error) {
    console.error('GET request failed:', {
      url: fullUrl,
      error,
    });
    throw error;
  }
}

export async function postJSON<T = any, D extends DataObject = DataObject>(url: string, data?: D, options: RequestOptions = {}): Promise<T> {
  return sendJSON<T, D>('POST', url, data, options);
}

export async function putJSON<T = any, D extends DataObject = DataObject>(url: string, data?: D, options: RequestOptions = {}): Promise<T> {
  return sendJSON<T, D>('PUT', url, data, options);
}

export async function deleteJSON<T = any, D extends DataObject = DataObject>(url: string, data?: D, options: RequestOptions = {}): Promise<T> {
  return sendJSON<T, D>('DELETE', url, data, options);
}

async function sendJSON<T = any, D extends DataObject = DataObject>(method: string, url: string, data?: D, options: RequestOptions = {}): Promise<T> {
  const { headers: customHeaders, returnStatusCode = false, bodyType = BodyType.json, skipAuth = false } = options;

  const fullUrl = url.startsWith('http') ? url : `${API_CONFIG.baseUrl}${url}`;
  const headers = await buildHeaders(customHeaders, skipAuth);

  let body: string | FormData | undefined;

  if (data && bodyType !== BodyType.none) {
    if (bodyType === BodyType.json) {
      headers.set('Content-Type', 'application/json');
      body = JSON.stringify(data);
    } else if (bodyType === BodyType.formUrlEncoded) {
      headers.set('Content-Type', 'application/x-www-form-urlencoded');
      const params = new URLSearchParams();
      Object.entries(data).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          params.append(key, String(value));
        }
      });
      body = params.toString();
    }
  }

  try {
    const response = await fetch(fullUrl, {
      method,
      headers,
      body,
      signal: AbortSignal.timeout(API_CONFIG.timeout),
    });

    if (!response.ok) {
      await handleError(fullUrl, response);
    }

    const result = await handleSuccess(response);

    if (returnStatusCode) {
      return { ...result, statusCode: response.status } as T;
    }

    return result as T;
  } catch (error) {
    console.error(`${method} request failed:`, {
      url: fullUrl,
      data: maskSensitiveData(data),
      error,
    });
    throw error;
  }
}

export { HttpError };
